import { Money } from '@palaccount/money';

/**
 * FIN-06 §5b — the two fee modes, mirrored client side so the two summary
 * tiles update as the user types (the server recomputes at posting; this is
 * drawing, not authority — BR-PERM-49).
 *
 *   deduct   source pays `amount`;       destination receives (amount − fee) × rate
 *   expense  source pays `amount + fee`; destination receives amount × rate
 *
 * A fee larger than the amount clamps the net at zero (BR-FIN-06-11). The
 * converted amount is computed from the unrounded rate and rounded ONCE at
 * the end (FIN-06 §15).
 *
 * DECIMAL, not JS numbers. This module used to read the three inputs with
 * `Number()` and add, subtract and multiply them as floats, rounding with
 * `Math.round(v * 100) / 100`. That is the same defect the POS cart shipped
 * with — 22.10 at 15% floats to 3.31499… and rounds DOWN a cent — except here
 * the figure is a bank transfer's net, and the screen quoting the wrong cent
 * is the one the treasurer reads before approving it. Every amount is a Money
 * from the door in.
 */

export type FeeMode = 'deduct' | 'expense' | null;

export interface TransferTotals {
  /** Source currency. */
  totalFromSource: string;
  /** Destination currency (= source when the currencies match). */
  netToDestination: string;
  /** amount × rate, destination currency; null when same-currency. */
  convertedAmount: string | null;
}

/**
 * The screen's inputs are free text, so a half-typed or empty box is normal
 * and must read as zero rather than throw.
 *
 * The ARITHMETIC TAG is nominal and REACHES NO COLUMN: these tiles mirror one
 * transfer, whose two sides the server tags for real (`cash-transfers.service`
 * reads `baseCurrencyCode(tx)`), and whose two account currencies the editor
 * LABELS from the accounts themselves.
 */
const TAG = 'USD';
const DECIMAL_TEXT = /^-?\d+(\.\d+)?$/;

function moneyOrZero(raw: string): Money {
  const trimmed = raw.trim();
  return DECIMAL_TEXT.test(trimmed) ? Money.of(trimmed, TAG) : Money.zero(TAG);
}

/**
 * A rate is a multiplier, not an amount — Money.times takes it as a string.
 *
 * ERR-FIN-06-05 — this is the line the row describes: rubbish in the rate box
 * "counts as a manual rate of zero, and the converted amount becomes 0.00".
 * The 0.00 is deliberate and honest — a preview that guessed a rate for text
 * it cannot read would be quoting money nobody entered — and the row's remedy
 * ("Click reset") is the BR-FIN-06-06 reset link beside the box. The server
 * is not fooled either way: the contract's rate regex refuses a non-numeric
 * rate at save, so the zero never leaves the screen.
 */
function rateOrZero(raw: string): string {
  const trimmed = raw.trim();
  return DECIMAL_TEXT.test(trimmed) ? trimmed : '0';
}

export function transferTotals(input: {
  amount: string;
  fee: string;
  feeMode: FeeMode;
  rate: string;
  crossCurrency: boolean;
}): TransferTotals {
  const amount = moneyOrZero(input.amount);
  const fee = moneyOrZero(input.fee);
  const rate = input.crossCurrency ? rateOrZero(input.rate) : '1';
  const feeActive = fee.isPositive() && input.feeMode !== null;

  const totalFromSource =
    feeActive && input.feeMode === 'expense' ? amount.plus(fee) : amount;
  let netBase = feeActive && input.feeMode === 'deduct' ? amount.minus(fee) : amount;
  // BR-FIN-06-11: never a negative net.
  if (netBase.isNegative()) netBase = Money.zero(TAG);

  return {
    totalFromSource: totalFromSource.toFixed2(),
    // §15: multiply at full precision, round ONCE at the end.
    netToDestination: netBase.times(rate).toFixed2(),
    convertedAmount: input.crossCurrency ? amount.times(rate).toFixed2() : null,
  };
}
